import React, { FormEvent, useEffect, useState } from "react";
import { useAuth } from "../../../firebase/AuthProvider";
import { useLocation, useNavigate, Link, useSearchParams } from "react-router-dom";
import { getAuth, getMultiFactorResolver, MultiFactorResolver, applyActionCode } from "firebase/auth";
import { resendVerificationEmail } from "../../../services/userServices/SignUpService";
import { toast } from "react-toastify";

// Lucide Icons

import { Eye, EyeOff, Loader2, CheckCircle2, XCircle } from "lucide-react";

// Components & Assets
import MfaVerifyPage from "./MfaVerifyPage";
import ErrorTooltip from "./ErrorTooltip";
import landingimg from "../../../assets/images/Login-main.png";
import landingimgDark from "../../../assets/images/Login-main-dark.png";
import { WaiveraLogo } from "../../ui/Logo/WaiveraLogo";
import { BrandLoader } from "../../ui/Logo/BrandLoader";
import { PASSWORD_POLICY_SUMMARY, passwordMeetsPolicy } from "../../../utilities/passwordPolicy";
import { postAuthenticationDestination } from "../../../utilities/navigationDestinations";

const Login: React.FC = () => {
    const { login, currentUser, isLoading: isAuthLoading } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams(); // <-- Added to read URL params

    // --- Authentication State ---
    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [, setError] = useState<string>("");
    const [showPassword, setShowPassword] = useState(false);
    const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
    void (useState<boolean>(true));
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [fieldErrors, setFieldErrors] = useState<{
        email?: string;
        password?: string;
        terms?: string;
    }>({});

    // --- Email Verification State ---
    const [isVerifying, setIsVerifying] = useState<boolean>(false);
    const [verificationSuccess, setVerificationSuccess] = useState<boolean>(false);
    const [verificationError, setVerificationError] = useState<string>("");
    const [isResendingVerification, setIsResendingVerification] = useState<boolean>(false);

    const from = postAuthenticationDestination((location.state as any)?.from?.pathname);

    // The login route is side-effect free. An authenticated user who follows a
    // login link returns to the application without disrupting their other tabs.
    useEffect(() => {
        if (!isAuthLoading && currentUser && !isLoading) {
            navigate(from, { replace: true });
        }
    }, [currentUser, from, isAuthLoading, isLoading, navigate]);

    // 2. Intercept and Handle Email Verification (oobCode)
    useEffect(() => {
        const oobCode = searchParams.get('oobCode');

        if (oobCode) {
            const verifyEmail = async () => {
                setIsVerifying(true);
                try {
                    const auth = getAuth();
                    await applyActionCode(auth, oobCode);
                    setVerificationSuccess(true);
                    toast.success("Email verified successfully! Please log in to continue.");

                    // Clean up the URL so it doesn't try to re-verify if they refresh
                    searchParams.delete('oobCode');
                    setSearchParams(searchParams, { replace: true });
                } catch (err: any) {
                    console.error("Verification error:", err);
                    let errorMessage = "Invalid or expired verification link.";
                    if (err.code === "auth/expired-action-code") {
                        errorMessage = "The verification link has expired. Please request a new one.";
                    } else if (err.code === "auth/invalid-action-code") {
                        errorMessage = "The verification link is invalid. Please check the link or request a new one.";
                    } else if (err.code === "auth/user-disabled") {
                        errorMessage = "This account has been disabled. Please contact support.";
                    } else if (err.message) {
                        errorMessage = err.message;
                    }
                    setVerificationError(errorMessage);
                    // Don't toast here, we'll show it in the UI
                } finally {
                    setIsVerifying(false);
                }
            };

            verifyEmail();
        }
    }, [searchParams, setSearchParams]);


    const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        setFieldErrors({});
        const errors: { email?: string; password?: string; terms?: string } = {};

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email) {
            errors.email = "Please enter your email";
        } else if (!emailRegex.test(email)) {
            errors.email = "Please enter a valid email address";
        }

        if (!password) {
            errors.password = "Please enter your password";
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }

        try {
            setIsLoading(true);
            await login(email, password);

            // AuthProvider performs backend login and account initialization.
            // Do not repeat that request here: a failure in the duplicate call
            // left Firebase authenticated while this page remained at /login.
            navigate(from, { replace: true });
        } catch (err: any) {
            if (err.code === "auth/multi-factor-auth-required") {
                const resolver = getMultiFactorResolver(getAuth(), err);
                setMfaResolver(resolver);
            } else {
                const fieldError = mapAuthErrorToField(err);

                if (fieldError) {
                    setFieldErrors(fieldError);
                } else {
                    const msg = getFirebaseLoginErrorMessage(err);
                    setError(msg);
                    toast.error(msg);
                }
                console.error(err);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const getFirebaseLoginErrorMessage = (err: any): string => {
        const code = err?.code;
        switch (code) {
            case "auth/invalid-email":
            case "auth/user-not-found":
            case "auth/wrong-password":
            case "auth/invalid-credential":
                return "Invalid email or password";
            case "auth/weak-password":
                return `Your password does not meet the current requirements. ${PASSWORD_POLICY_SUMMARY}`;
            case "auth/user-disabled":
                return "This account is disabled. Contact support.";
            case "auth/too-many-requests":
                return "Too many failed attempts. Try again later.";
            default:
                return "Failed to login. Please try again.";
        }
    };

    const mapAuthErrorToField = (err: any) => {
        const code = err?.code;
        switch (code) {
            case "auth/invalid-email":
            case "auth/user-not-found":
                return { email: "Invalid email address" };
            case "auth/wrong-password":
            case "auth/invalid-credential":
                return { password: "Incorrect password" };
            case "auth/weak-password":
                return { password: `Password does not meet the current requirements. ${PASSWORD_POLICY_SUMMARY}` };
            case "auth/user-disabled":
                return { email: "This account is disabled" };
            default:
                return null;
        }
    };

    const handleResendVerification = async () => {
        if (!email) {
            setError("Please enter your email first.");
            return;
        }
        setIsResendingVerification(true);
        try {
            const response = await resendVerificationEmail({ email });
            if (response.message === "Already verified.") {
                toast.info("Your email is already verified. You can log in now.");
                setVerificationError("");
                setVerificationSuccess(true);
            } else {
                toast.success("Verification email resent!");
            }
        } catch (error: any) {
            toast.error(error.message || "Failed to resend. Please try again later.");
        } finally {
            setIsResendingVerification(false);
        }
    };

    const togglePassword = () => setShowPassword((prev) => !prev);

    // Firebase persistence is still resolving after a refresh. Showing the
    // login form here implies that the session was lost and causes a visible
    // form-to-dashboard flash for already-authenticated users.
    if (isAuthLoading && !isVerifying && !mfaResolver) {
        return <BrandLoader fullScreen label="Restoring your session" />;
    }

    return (
        <div className="min-h-screen flex bg-white font-inter">
            {/* LEFT SECTION (Form) */}
            <div className="flex-1 flex flex-col justify-center items-center px-8 md:px-16 lg:px-24 py-12">
                <div className="w-full max-w-md flex flex-col">
                    {/* Logo */}
                    <div className="mb-12 flex justify-start">
                        <WaiveraLogo />
                    </div>

                    {/* INTERCEPT STATE: Email Verification in progress */}
                    {isVerifying ? (
                        <div className="flex flex-col items-center justify-center py-10 text-center animate-in fade-in duration-500">
                            <Loader2 size={48} className="text-[#7E57C2] animate-spin mb-6" />
                            <h2 className="text-2xl font-semibold text-gray-900 mb-2 font-poppins">Verifying Email</h2>
                            <p className="text-gray-500">Please wait while we confirm your email address...</p>
                        </div>
                    ) : mfaResolver ? (
                        <MfaVerifyPage
                            resolver={mfaResolver}
                            onVerificationComplete={() => navigate(from, { replace: true })}
                            onCancel={() => setMfaResolver(null)}
                        />
                    ) : (
                        <div className="animate-in fade-in duration-500">

                            {/* SUCCESS STATE: Verification complete banner */}
                            {verificationSuccess && (
                                <div className="mb-8 p-4 bg-emerald-50 border border-emerald-100 rounded-xl flex items-start gap-3">
                                    <CheckCircle2 className="text-emerald-500 shrink-0 mt-0.5" size={20} />
                                    <div>
                                        <h3 className="text-sm font-semibold text-emerald-800">Email Verified</h3>
                                        <p className="text-xs text-emerald-600 mt-1">Your email address has been successfully verified. Please sign in to continue.</p>
                                    </div>
                                </div>
                            )}

                            {/* ERROR STATE: Verification failed banner */}
                            {verificationError && (
                                <div className="mb-8 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3">
                                    <XCircle className="text-red-500 shrink-0 mt-0.5" size={20} />
                                    <div className="w-full">
                                        <h3 className="text-sm font-semibold text-red-800">Verification Failed</h3>
                                        <p className="text-xs text-red-600 mt-1">{verificationError}</p>
                                        <button
                                            onClick={handleResendVerification}
                                            disabled={isResendingVerification || !email}
                                            className="text-xs font-bold text-red-700 hover:text-red-800 mt-2 inline-block underline disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {isResendingVerification ? 'Sending...' : 'Request a new link'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="mb-8">
                                <h1 className="text-2xl font-semibold text-gray-900 mb-2 font-poppins">Welcome back</h1>
                                <p className="text-gray-500 text-sm">Please enter your details to sign in.</p>
                            </div>

                            <form onSubmit={handleLogin} noValidate className="space-y-5">
                                {/* EMAIL */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        Email
                                    </label>
                                    <div className="relative">
                                        {fieldErrors.email && <ErrorTooltip message={fieldErrors.email} />}
                                        <input
                                            type="email"
                                            placeholder="Enter your email"
                                            value={email}
                                            onChange={(e) => {
                                                setEmail(e.target.value);
                                                setFieldErrors((prev) => ({ ...prev, email: undefined }));
                                            }}
                                            required
                                            autoComplete="username"
                                            className={`w-full h-12 bg-white rounded-xl px-4 text-sm text-gray-900 placeholder-gray-400 border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#7E57C2]/20 focus:border-[#7E57C2] ${fieldErrors.email ? "border-red-400" : "border-gray-200"
                                                }`}
                                        />
                                    </div>
                                </div>

                                {/* PASSWORD */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        Password
                                    </label>
                                    <div className="relative">
                                        {fieldErrors.password && <ErrorTooltip message={fieldErrors.password} />}
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={(e) => {
                                                setPassword(e.target.value);
                                                setFieldErrors((prev) => ({ ...prev, password: undefined }));
                                            }}
                                            required
                                            autoComplete="current-password"
                                            aria-describedby={password && !passwordMeetsPolicy(password) ? "login-password-policy-note" : undefined}
                                            className={`w-full h-12 bg-white rounded-xl pl-4 pr-12 text-sm text-gray-900 placeholder-gray-400 border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#7E57C2]/20 focus:border-[#7E57C2] ${fieldErrors.password ? "border-red-400" : "border-gray-200"
                                                }`}
                                        />
                                        <button
                                            type="button"
                                            onClick={togglePassword}
                                            className="absolute right-0 top-0 h-full px-4 text-gray-400 hover:text-gray-600 flex items-center justify-center transition-colors"
                                        >
                                            {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
                                        </button>
                                    </div>
                                </div>

                                {/* FORGOT PASSWORD & CHECKBOX CONTAINER */}
                                <div className="flex flex-col gap-4 mt-2">

                                    {/* TERMS DISCLAIMER */}
                                    <div className="text-xs text-gray-500 leading-relaxed mt-2">
                                        By signing in, you agree to our{" "}
                                        <Link to="/terms-of-service" className="font-medium text-[#7E57C2] hover:underline">
                                            Terms of Service
                                        </Link>{" "}
                                        and{" "}
                                        <Link to="/privacy-policy" className="font-medium text-[#7E57C2] hover:underline">
                                            Privacy Policy
                                        </Link>.
                                    </div>
                                    {fieldErrors.terms && (
                                        <p className="text-xs text-red-500 mt-1">{fieldErrors.terms}</p>
                                    )}
                                </div>

                                {/* SUBMIT BUTTON */}
                                <div className="pt-4">
                                    <button
                                        type="submit"
                                        disabled={isLoading}
                                        className="w-full h-12 bg-[#7E57C2] hover:bg-[#6b46ac] text-white rounded-xl text-[15px] font-medium transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center shadow-sm hover:shadow-md"
                                    >
                                        {isLoading ? "Signing in..." : "Sign in"}
                                    </button>
                                </div>
                                <div className="flex justify-center text-center items-center w-full mt-1">
                                    <Link
                                        to="/forgot-password"
                                        className="text-sm font-medium text-[#7E57C2] hover:text-[#6b46ac] hover:underline transition-all"
                                    >
                                        Forgot Password?
                                    </Link>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            </div>

            {/* RIGHT SECTION (Image) */}
            <div className="hidden lg:block lg:w-[54%] xl:w-[56%] relative overflow-hidden bg-gray-900">
                {/* Background fill prevents empty bars around the contained image */}
                <img
                    src={landingimg}
                    alt=""
                    aria-hidden="true"
                    className="dark:hidden 
                        absolute inset-0
                        h-full w-full
                        scale-110
                        object-cover
                        object-center
                        blur-xl
                        opacity-40
                    "
                />
                <img
                    src={landingimgDark}
                    alt=""
                    aria-hidden="true"
                    className="hidden dark:block 
                        absolute inset-0
                        h-full w-full
                        scale-110
                        object-cover
                        object-center
                        blur-xl
                        opacity-40
                    "
                />

                {/* Optional subtle overlay keeps the background fill unobtrusive */}
                <div className="absolute inset-0 bg-black/10" />

                {/* Foreground image remains fully readable */}
                <div className="absolute inset-0 flex items-center justify-center p-3 xl:p-5">
                    <img
                        src={landingimg}
                        alt="Landing Presentation"
                        className="dark:hidden 
                            h-full w-full
                            object-contain
                            object-center
                        "
                    />
                    <img
                        src={landingimgDark}
                        alt="Landing Presentation"
                        className="hidden dark:block 
                            h-full w-full
                            object-contain
                            object-center
                        "
                    />
                </div>
            </div>
        </div>
    );
};

export default Login;
